import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  Loader2,
  PenLine,
  Plus,
  Search,
  Trash2,
  MapPin,
  RotateCcw,
  SlidersHorizontal,
  Layers,
  MapPinOff,
  CheckCircle2,
  X,
  ShieldCheck,
  Check,
  AlertCircle,
} from "lucide-react";
import useDebounce from "@/hooks/useDebounce";
import useAuth from "@/hooks/useAuth";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@radix-ui/react-separator";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Paginat from "@/components/lib/paginat";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import { restaurant_id as fallbackRestaurantId } from "@/api/lib/url";
import { findDelivaryPostcodes } from "@/api/settings/delivary-management";
import DeliveryCoverageSummary from "./components/DeliveryCoverageSummary";
import {
  clearDeliveryManagementMessages,
  createDeliveryCondition,
  deleteDeliveryCondition,
  fetchDeliveryConditions,
  fetchDeliveryCoverageSummary,
  toggleDeliveryCondition,
  updateDeliveryCondition,
} from "@/redux/slice/settings/delivary-management-slice";

const CHARGE_TYPES = [
  { value: "include", label: "Include" },
  { value: "exclude", label: "Exclude" },
];

const formatCurrency = (val) => {
  if (val === undefined || val === null || val === "" || isNaN(Number(val))) return "-";
  return `£${Number(val).toFixed(2)}`;
};

const emptyConditionForm = {
  post_code: "",
  minimum_order: "",
  free_delivery_above: "",
  charge_type: "include",
  charge: "",
  driver_fee: "",
};

const emptyBulkForm = {
  minimum_order: "",
  free_delivery_above: "",
  charge_type: "include",
  charge: "",
  driver_fee: "",
};

const normalizeNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

function DeliveryManagement() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const restaurantId =
    user?.restaurant_id || localStorage.getItem("restaurant_id") || fallbackRestaurantId;

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState("10");
  const [listFilters, setListFilters] = useState({
    starting_distance: "",
    ending_distance: "",
    post_code_start_with: "",
    status: "active",
  });
  const [appliedListFilters, setAppliedListFilters] = useState({
    starting_distance: "",
    ending_distance: "",
    post_code_start_with: "",
    status: "active",
  });

  const [postcodeFilters, setPostcodeFilters] = useState({
    starting_distance: "",
    ending_distance: "",
    post_code_start_with: "",
  });

  const [postcodes, setPostcodes] = useState([]);
  const [postcodesMeta, setPostcodesMeta] = useState({});
  const [postcodesLoading, setPostcodesLoading] = useState(false);
  const postcodesLoadingStartRef = useRef(0);
  const postcodesLoadingTimeoutRef = useRef(null);

  const [selectedPostcodes, setSelectedPostcodes] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState(emptyBulkForm);
  const [bulkOverrideMeta, setBulkOverrideMeta] = useState(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(emptyConditionForm);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [conditionToDelete, setConditionToDelete] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const debouncedSearch = useDebounce(appliedListFilters.post_code_start_with, 350);

  const deliveryState = useSelector(
    (state) => state.settings?.delivaryManagement || {},
  );

  const {
    conditions = [],
    pagination = null,
    coverageSummary = null,
    coverageLoading = false,
    listLoading = false,
    createLoading = false,
    updateLoading = false,
    deleteLoading = false,
    toggleLoading = false,
    error = null,
    success = null,
  } = deliveryState;

  const totalSelected = selectedPostcodes.length;
  const selectedPostcodeTotalCount = useMemo(() => {
    return selectedPostcodes.reduce((sum, code) => {
      const item = postcodes.find((row) => row.postcode_first_part === code);
      const count = Number(item?.postcode_count || 0);
      return sum + (Number.isNaN(count) ? 0 : count);
    }, 0);
  }, [selectedPostcodes, postcodes]);
  const allSelected = postcodes.length > 0 && totalSelected === postcodes.length;

  const startIndex = useMemo(() => {
    const page = pagination?.current_page || 1;
    const per = pagination?.per_page || Number(perPage) || 10;
    return (page - 1) * per;
  }, [pagination, perPage]);

  useEffect(() => {
    if (!restaurantId) return;
    dispatch(fetchDeliveryCoverageSummary(restaurantId));
    dispatch(
      fetchDeliveryConditions({
        restaurantId,
        params: {
          page: currentPage,
          per_page: Number(perPage),
          measure_by: "radius",
          status: appliedListFilters.status === "all" ? undefined : (appliedListFilters.status || undefined),
          starting_distance: appliedListFilters.starting_distance || undefined,
          ending_distance: appliedListFilters.ending_distance || undefined,
          search_term: debouncedSearch || undefined,
        },
      }),
    );
  }, [
    dispatch,
    restaurantId,
    currentPage,
    perPage,
    appliedListFilters.starting_distance,
    appliedListFilters.ending_distance,
    appliedListFilters.status,
    debouncedSearch,
  ]);

  useEffect(() => {
    if (!error && !success) return;
    if (error) toast.error(error);
    if (success) toast.success(success);
    dispatch(clearDeliveryManagementMessages());
  }, [error, success, dispatch]);

  const applyListSearch = () => {
    setCurrentPage(1);
    setAppliedListFilters({ ...listFilters });
  };

  const handleResetListFilters = () => {
    const defaultFilters = {
      starting_distance: "",
      ending_distance: "",
      post_code_start_with: "",
      status: "active",
    };
    setListFilters(defaultFilters);
    setAppliedListFilters(defaultFilters);
    setCurrentPage(1);
    if (restaurantId) {
      dispatch(
        fetchDeliveryConditions({
          restaurantId,
          params: {
            page: 1,
            per_page: Number(perPage),
            measure_by: "radius",
            status: "active",
          },
        }),
      );
    }
  };

  const handleFindPostcodes = async (overrideFilters = null) => {
    if (!restaurantId) return;
    const filtersToUse = overrideFilters || postcodeFilters;
    if (postcodesLoadingTimeoutRef.current) {
      clearTimeout(postcodesLoadingTimeoutRef.current);
    }
    postcodesLoadingStartRef.current = Date.now();
    setPostcodesLoading(true);
    setSelectedPostcodes([]);
    const res = await findDelivaryPostcodes(restaurantId, {
      starting_distance: filtersToUse.starting_distance || undefined,
      ending_distance: filtersToUse.ending_distance || undefined,
      post_code_start_with: filtersToUse.post_code_start_with || undefined,
    });
    if (res?.status === "success") {
      setPostcodes(res?.data?.items || []);
      setPostcodesMeta({
        count: res?.data?.count,
        total: res?.data?.total,
        starting_distance: res?.data?.starting_distance,
        ending_distance: res?.data?.ending_distance,
        post_code_start_with: res?.data?.post_code_start_with,
      });
    } else {
      setPostcodes([]);
      setPostcodesMeta({});
      toast.error(res?.message || "Failed to fetch post codes");
    }
    const elapsed = Date.now() - postcodesLoadingStartRef.current;
    const delay = Math.max(0, 600 - elapsed);
    postcodesLoadingTimeoutRef.current = setTimeout(() => {
      setPostcodesLoading(false);
    }, delay);
  };

  const handleSelectCoverageRange = ({ starting_distance, ending_distance, post_code_start_with }) => {
    const nextFilters = {
      starting_distance: starting_distance ?? "",
      ending_distance: ending_distance ?? "",
      post_code_start_with: post_code_start_with ?? "",
    };
    setPostcodeFilters(nextFilters);
    handleFindPostcodes(nextFilters);
  };

  const handleRefreshCoverage = () => {
    if (restaurantId) {
      dispatch(fetchDeliveryCoverageSummary(restaurantId));
    }
  };

  const handleLoadBandPostcodes = async ({ starting_distance, ending_distance }) => {
    if (!restaurantId) return null;
    const res = await findDelivaryPostcodes(restaurantId, {
      starting_distance,
      ending_distance,
    });
    if (res?.status === "success") {
      return res.data;
    }
    return null;
  };

  const handleConfigureFromCoverage = ({
    starting_distance,
    ending_distance,
    postcodes: groupsToConfig,
    totalSelectedCount,
    totalServerCount,
  }) => {
    if (!groupsToConfig || !groupsToConfig.length) {
      toast.error("Please select at least one post code group to configure.");
      return;
    }
    setPostcodeFilters((prev) => ({
      ...prev,
      starting_distance: String(starting_distance ?? ""),
      ending_distance: String(ending_distance ?? ""),
    }));
    setSelectedPostcodes(groupsToConfig);
    setBulkOverrideMeta({
      totalSelectedCount: totalSelectedCount ?? groupsToConfig.length,
      totalServerCount: totalServerCount ?? groupsToConfig.length,
    });
    setBulkForm(emptyBulkForm);
    setBulkDialogOpen(true);
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedPostcodes([]);
      return;
    }
    setSelectedPostcodes(postcodes.map((item) => item.postcode_first_part));
  };

  const togglePostcodeSelection = (code) => {
    setSelectedPostcodes((prev) =>
      prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code],
    );
  };

  const openBulkConfigure = () => {
    if (!selectedPostcodes.length) {
      toast.error("Select post codes to configure.");
      return;
    }
    setBulkOverrideMeta(null);
    setBulkForm(emptyBulkForm);
    setBulkDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditTarget(null);
    setEditForm(emptyConditionForm);
    setEditDialogOpen(true);
  };

  const openEditDialog = (condition) => {
    setEditTarget(condition);
    setEditForm({
      post_code: condition?.post_code || "",
      minimum_order: condition?.minimum_order ?? "",
      free_delivery_above: condition?.free_delivery_above ?? "",
      charge_type: condition?.charge_type || "include",
      charge: condition?.charge ?? "",
      driver_fee: condition?.driver_fee ?? "",
    });
    setEditDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!conditionToDelete) return;
    const res = await dispatch(deleteDeliveryCondition(conditionToDelete.id));
    if (!res.error) {
      setConditionToDelete(null);
      setDeleteDialogOpen(false);
      dispatch(fetchDeliveryCoverageSummary(restaurantId));
    }
  };

  const handleToggleStatus = async (condition) => {
    if (!condition?.id) return;
    setTogglingId(condition.id);
    const res = await dispatch(toggleDeliveryCondition(condition.id));
    if (!res.error) {
      dispatch(fetchDeliveryCoverageSummary(restaurantId));
      dispatch(
        fetchDeliveryConditions({
          restaurantId,
          params: {
            page: currentPage,
            per_page: Number(perPage),
            measure_by: "radius",
            status: appliedListFilters.status === "all" ? undefined : (appliedListFilters.status || undefined),
            starting_distance: appliedListFilters.starting_distance || undefined,
            ending_distance: appliedListFilters.ending_distance || undefined,
            search_term: debouncedSearch || undefined,
          },
        }),
      );
    }
    setTogglingId(null);
  };

  const buildConditionPayload = (form, extra = {}) => ({
    ...extra,
    post_code: form.post_code,
    minimum_order: normalizeNumber(form.minimum_order),
    free_delivery_above: normalizeNumber(form.free_delivery_above),
    charge_type: form.charge_type,
    charge: normalizeNumber(form.charge),
    driver_fee: normalizeNumber(form.driver_fee),
    status: extra.status || "active",
  });

  const validateForm = (form, requirePostcode = true) => {
    if (requirePostcode && !String(form.post_code || "").trim()) {
      toast.error("Post code is required.");
      return false;
    }
    if (String(form.minimum_order || "").trim() === "") {
      toast.error("Min order value is required.");
      return false;
    }
    if (form.charge_type === "exclude") {
      if (String(form.charge || "").trim() === "") {
        toast.error("Delivery charge is required.");
        return false;
      }
      if (String(form.driver_fee || "").trim() === "") {
        toast.error("Driver fee is required.");
        return false;
      }
    }
    return true;
  };

  const submitBulkConfigure = async () => {
    if (!selectedPostcodes.length) {
      toast.error("Select post codes to configure.");
      return;
    }
    if (!validateForm({ ...bulkForm, post_code: "bulk" }, false)) return;

    const payload = {
      restaurant_id: restaurantId,
      post_code: selectedPostcodes,
      starting_distance: normalizeNumber(postcodeFilters.starting_distance || postcodesMeta?.starting_distance),
      ending_distance: normalizeNumber(postcodeFilters.ending_distance || postcodesMeta?.ending_distance),
      minimum_order: normalizeNumber(bulkForm.minimum_order),
      free_delivery_above: normalizeNumber(bulkForm.free_delivery_above),
      charge_type: bulkForm.charge_type,
      charge: normalizeNumber(bulkForm.charge),
      driver_fee: normalizeNumber(bulkForm.driver_fee),
      status: "active",
    };

    const res = await dispatch(createDeliveryCondition(payload));
    if (!res.error) {
      setBulkDialogOpen(false);
      setSelectedPostcodes([]);
      setBulkOverrideMeta(null);
      dispatch(fetchDeliveryCoverageSummary(restaurantId));
      if (postcodeFilters.starting_distance !== "" || postcodeFilters.ending_distance !== "") {
        handleFindPostcodes();
      }
      dispatch(
        fetchDeliveryConditions({
          restaurantId,
          params: {
            page: currentPage,
            per_page: Number(perPage),
            measure_by: "radius",
            status: appliedListFilters.status === "all" ? undefined : (appliedListFilters.status || undefined),
            starting_distance: appliedListFilters.starting_distance || undefined,
            ending_distance: appliedListFilters.ending_distance || undefined,
            search_term: debouncedSearch || undefined,
          },
        }),
      );
    }
  };

  const submitEditForm = async () => {
    if (!validateForm(editForm, true)) return;

    if (editTarget?.id) {
      const payload = buildConditionPayload(editForm, {
        status: editTarget?.status || "active",
        starting_distance: editTarget?.starting_distance ?? 0,
        ending_distance: editTarget?.ending_distance ?? 0,
      });
      const res = await dispatch(
        updateDeliveryCondition({ id: editTarget.id, payload }),
      );
      if (!res.error) {
        setEditDialogOpen(false);
        dispatch(fetchDeliveryCoverageSummary(restaurantId));
      }
      return;
    }

    const payload = buildConditionPayload(editForm, {
      restaurant_id: restaurantId,
      starting_distance: normalizeNumber(listFilters.starting_distance),
      ending_distance: normalizeNumber(listFilters.ending_distance),
    });
    const res = await dispatch(createDeliveryCondition(payload));
    if (!res.error) {
      setEditDialogOpen(false);
      dispatch(fetchDeliveryCoverageSummary(restaurantId));
    }
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Delivery Management" }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Settings", path: "/backoffice/settings" },
        {
          name: "Delivery Management",
          path: "/backoffice/settings/delivery-management",
        },
      ]}
    >
      <div className="container mt-4 pb-6">
        <Tabs
          valueKey="delivery-management"
          className="bg-transparent relative"
          defaultValue="post-code-list"
        >
          <TabsList className="grid grid-cols-6 w-full">
            <Separator className="absolute top-8" />
            <TabsTrigger className="bg-transparent" value="configure-post-code">
              Postcode List
            </TabsTrigger>
            <TabsTrigger className="bg-transparent" value="post-code-list" >
              Configure Postcode
            </TabsTrigger>
          </TabsList>
          <Separator className="absolute top-8 w-full h-[1px] bg-neutral-700" />

          <TabsContent value="post-code-list" className="mt-5 space-y-6">
            {/* Delivery Coverage Dashboard */}
            <DeliveryCoverageSummary
              coverageData={coverageSummary}
              isLoading={coverageLoading}
              onRefresh={handleRefreshCoverage}
              onConfigure={handleConfigureFromCoverage}
              onLoadBandPostcodes={handleLoadBandPostcodes}
            />
          </TabsContent>

          <TabsContent value="configure-post-code" className="mt-5 space-y-5">
            {/* Top Mini Summary Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-neutral-800 bg-cblack3/80 p-3.5 flex items-center justify-between shadow-md">
                <div>
                  <div className="text-[11px] font-medium text-cwhite3 uppercase tracking-wider">
                    Total Configured Postcodes
                  </div>
                  <div className="text-xl font-bold text-cwhite mt-0.5">
                    {(pagination?.total || conditions.length).toLocaleString()}
                  </div>
                </div>
                <div className="h-9 w-9 rounded-lg bg-cyellow/10 border border-cyellow/30 flex items-center justify-center text-cyellow">
                  <MapPin size={18} />
                </div>
              </div>

              <div className="rounded-xl border border-neutral-800 bg-cblack3/80 p-3.5 flex items-center justify-between shadow-md">
                <div>
                  <div className="text-[11px] font-medium text-cwhite3 uppercase tracking-wider">
                    Free Delivery Zones
                  </div>
                  <div className="text-xl font-bold text-emerald-400 mt-0.5">
                    {conditions.filter((c) => c.charge_type === "include").length}
                  </div>
                </div>
                <div className="h-9 w-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                  <CheckCircle2 size={18} />
                </div>
              </div>

              <div className="rounded-xl border border-neutral-800 bg-cblack3/80 p-3.5 flex items-center justify-between shadow-md">
                <div>
                  <div className="text-[11px] font-medium text-cwhite3 uppercase tracking-wider">
                    Charged Delivery Zones
                  </div>
                  <div className="text-xl font-bold text-sky-400 mt-0.5">
                    {conditions.filter((c) => c.charge_type === "exclude").length}
                  </div>
                </div>
                <div className="h-9 w-9 rounded-lg bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-400">
                  <Layers size={18} />
                </div>
              </div>
            </div>

            {/* Filter & Actions Bar */}
            <div className="rounded-xl border border-neutral-800 bg-cblack3/80 p-4 shadow-md space-y-3">
              <div className="flex flex-wrap items-end gap-3 justify-between">
                <div className="flex flex-wrap items-end gap-3">
                  {/* Search by Post Code */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-cwhite3">
                      Search Postcode
                    </span>
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-cwhite3"
                      />
                      <Input
                        value={listFilters.post_code_start_with}
                        onChange={(event) =>
                          setListFilters((prev) => ({
                            ...prev,
                            post_code_start_with: event.target.value,
                          }))
                        }
                        placeholder="e.g. NR1, NR10..."
                        className="h-10 pl-8 pr-7 w-44 bg-cblack2 border-neutral-700 text-xs focus-visible:ring-cyellow"
                      />
                      {listFilters.post_code_start_with && (
                        <button
                          type="button"
                          onClick={() =>
                            setListFilters((prev) => ({
                              ...prev,
                              post_code_start_with: "",
                            }))
                          }
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-cwhite3 hover:text-white"
                        >
                          <X size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Starting Distance */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-cwhite3">
                      Min Distance
                    </span>
                    <div className="relative">
                      <Input
                        value={listFilters.starting_distance}
                        onChange={(event) =>
                          setListFilters((prev) => ({
                            ...prev,
                            starting_distance: event.target.value,
                          }))
                        }
                        placeholder="0"
                        type="number"
                        className="h-10 pr-7 w-28 bg-cblack2 border-neutral-700 text-xs focus-visible:ring-cyellow"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-cwhite3/60">
                        mi
                      </span>
                    </div>
                  </div>

                  {/* Ending Distance */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-cwhite3">
                      Max Distance
                    </span>
                    <div className="relative">
                      <Input
                        value={listFilters.ending_distance}
                        onChange={(event) =>
                          setListFilters((prev) => ({
                            ...prev,
                            ending_distance: event.target.value,
                          }))
                        }
                        placeholder="10"
                        type="number"
                        className="h-10 pr-7 w-28 bg-cblack2 border-neutral-700 text-xs focus-visible:ring-cyellow"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-cwhite3/60">
                        mi
                      </span>
                    </div>
                  </div>

                  {/* Status Selection Filter */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-cwhite3">
                      Status
                    </span>
                    <Select
                      value={listFilters.status}
                      onValueChange={(value) =>
                        setListFilters((prev) => ({
                          ...prev,
                          status: value,
                        }))
                      }
                    >
                      <SelectTrigger className="h-10 w-28 bg-cblack2 border-neutral-700 text-xs text-cwhite">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-cblack3 border-neutral-700 text-cwhite">
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={applyListSearch}
                    className="h-10 px-5 bg-cyellow text-black hover:bg-cyellow/90 font-medium text-xs shadow-sm flex items-center gap-1.5"
                  >
                    <Search size={14} />
                    Search
                  </Button>

                  {(listFilters.starting_distance ||
                    listFilters.ending_distance ||
                    listFilters.post_code_start_with ||
                    listFilters.status !== "active") && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleResetListFilters}
                      className="h-10 px-3 text-xs bg-cblack2 border-neutral-700 hover:bg-neutral-800 text-cwhite3 hover:text-white"
                      title="Reset filters"
                    >
                      <RotateCcw size={13} className="mr-1" />
                      Reset
                    </Button>
                  )}
                </div>

                {/* Right Action: Add Post Code + Per Page */}
                <div className="flex items-center gap-3">
                  <Button
                    onClick={openAddDialog}
                    className="h-10 px-4 bg-cyellow text-black hover:bg-cyellow/90 font-semibold text-xs shadow-md flex items-center gap-1.5"
                  >
                    <Plus size={15} />
                    Add Postcode
                  </Button>

                  <div className="flex items-center gap-2 text-xs text-cwhite3">
                    <span>Show</span>
                    <Select value={perPage} onValueChange={setPerPage}>
                      <SelectTrigger className="h-10 w-20 bg-cblack2 border-neutral-700 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-cblack3 border-neutral-700 text-cwhite">
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Glassmorphic Data Table Card */}
            <div className="rounded-xl border border-neutral-800 bg-cblack3/60 backdrop-blur-sm shadow-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-neutral-800 bg-neutral-900/80 hover:bg-neutral-900/80">
                    <TableHead className="w-12 text-xs font-semibold uppercase tracking-wider text-cwhite3 text-center">
                      #
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-cwhite3">
                      Postcode
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-cwhite3">
                      Distance
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-cwhite3">
                      Coordinates
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-cwhite3">
                      Min Order
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-cwhite3">
                      Free Above
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-cwhite3">
                      Charge Type
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-cwhite3">
                      Delivery Fee
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-cwhite3">
                      Driver Fee
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-cwhite3">
                      Status
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-cwhite3 text-right">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listLoading &&
                    [1, 2, 3, 4, 5].map((i) => (
                      <TableRow
                        key={`skeleton-${i}`}
                        className="border-b border-neutral-800/60"
                      >
                        <TableCell colSpan={11} className="py-4">
                          <div className="flex items-center gap-4 animate-pulse">
                            <div className="h-4 w-8 bg-neutral-800 rounded"></div>
                            <div className="h-6 w-20 bg-neutral-800 rounded"></div>
                            <div className="h-4 w-16 bg-neutral-800 rounded"></div>
                            <div className="h-4 w-28 bg-neutral-800 rounded"></div>
                            <div className="h-4 w-16 bg-neutral-800 rounded"></div>
                            <div className="h-4 w-16 bg-neutral-800 rounded"></div>
                            <div className="h-5 w-24 bg-neutral-800 rounded-full"></div>
                            <div className="h-4 w-16 bg-neutral-800 rounded"></div>
                            <div className="h-4 w-16 bg-neutral-800 rounded"></div>
                            <div className="h-4 w-16 bg-neutral-800 rounded"></div>
                            <div className="h-6 w-16 bg-neutral-800 rounded ml-auto"></div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}

                  {!listLoading && conditions.length === 0 && (
                    <TableRow className="border-b border-neutral-800/60 hover:bg-transparent">
                      <TableCell colSpan={11} className="py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <MapPinOff size={36} className="text-neutral-600 mb-2.5" />
                          <div className="text-sm font-medium text-cwhite">
                            No delivery conditions found
                          </div>
                          <div className="text-xs text-cwhite3 mt-1">
                            Try adjusting your search filters or add a new postcode condition.
                          </div>
                          {(listFilters.starting_distance ||
                            listFilters.ending_distance ||
                            listFilters.post_code_start_with) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleResetListFilters}
                              className="mt-3 text-xs bg-cblack2 border-neutral-700 hover:bg-neutral-800 text-cyellow"
                            >
                              Reset Filters
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}

                  {!listLoading &&
                    conditions.map((condition, index) => {
                      const isStatusActive =
                        String(condition.status).toLowerCase() === "active";
                      const isInclude = condition.charge_type === "include";

                      return (
                        <TableRow
                          key={condition.id}
                          className="border-b border-neutral-800/60 hover:bg-cblack4/80 transition-colors group"
                        >
                          {/* SL No */}
                          <TableCell className="text-center font-mono text-xs text-cwhite3/60">
                            #{(startIndex + index + 1).toString().padStart(2, "0")}
                          </TableCell>

                          {/* Post Code */}
                          <TableCell>
                            <span className="font-mono text-xs font-bold text-cyellow bg-cyellow/10 px-2.5 py-1 rounded-md border border-cyellow/30 tracking-wide inline-block shadow-sm">
                              {condition.post_code}
                            </span>
                          </TableCell>

                          {/* Distance */}
                          <TableCell>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 font-mono">
                              {condition.distance != null ? `${condition.distance} mi` : "-"}
                            </span>
                          </TableCell>

                          {/* Coordinates */}
                          <TableCell>
                            {condition.latitude || condition.longitude ? (
                              <div
                                className="flex items-center gap-1 text-xs text-cwhite3/80 font-mono"
                                title={`Lat: ${condition.latitude}, Lng: ${condition.longitude}`}
                              >
                                <MapPin size={12} className="text-cyellow/70 shrink-0" />
                                <span>
                                  {condition.latitude
                                    ? Number(condition.latitude).toFixed(4)
                                    : "-"}
                                  ,{" "}
                                  {condition.longitude
                                    ? Number(condition.longitude).toFixed(4)
                                    : "-"}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-cwhite3/40">-</span>
                            )}
                          </TableCell>

                          {/* Min Order Value */}
                          <TableCell className="text-xs font-medium text-cwhite">
                            {formatCurrency(condition.minimum_order)}
                          </TableCell>

                          {/* Free Delivery Above */}
                          <TableCell className="text-xs">
                            {condition.free_delivery_above &&
                            Number(condition.free_delivery_above) > 0 ? (
                              <span className="font-semibold text-emerald-400">
                                {formatCurrency(condition.free_delivery_above)}
                              </span>
                            ) : (
                              <span className="text-cwhite3/40">-</span>
                            )}
                          </TableCell>

                          {/* Charge Type */}
                          <TableCell>
                            {isInclude ? (
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-medium whitespace-nowrap">
                                Include (Free)
                              </span>
                            ) : (
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30 font-medium whitespace-nowrap">
                                Exclude (Fee)
                              </span>
                            )}
                          </TableCell>

                          {/* Delivery Charge */}
                          <TableCell className="text-xs">
                            {!isInclude ? (
                              <span className="text-amber-300 font-semibold">
                                {formatCurrency(condition.charge)}
                              </span>
                            ) : (
                              <span className="text-cwhite3/40">Included</span>
                            )}
                          </TableCell>

                          {/* Driver Fee */}
                          <TableCell className="text-xs">
                            {!isInclude ? (
                              <span className="text-cwhite font-medium">
                                {formatCurrency(condition.driver_fee)}
                              </span>
                            ) : (
                              <span className="text-cwhite3/40">Included</span>
                            )}
                          </TableCell>

                          {/* Status Switch Toggle */}
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={isStatusActive}
                                onCheckedChange={() => handleToggleStatus(condition)}
                                disabled={toggleLoading && togglingId === condition.id}
                                className="data-[state=checked]:bg-emerald-500 scale-90"
                              />
                              <span
                                className={`text-[11px] font-medium ${
                                  isStatusActive
                                    ? "text-emerald-400"
                                    : "text-neutral-500"
                                }`}
                              >
                                {isStatusActive ? "Active" : "Inactive"}
                              </span>
                            </div>
                          </TableCell>

                          {/* Action Buttons */}
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-cwhite3 hover:text-cyellow hover:bg-cyellow/15 rounded-lg transition-colors"
                                onClick={() => openEditDialog(condition)}
                                title="Edit Post Code Condition"
                              >
                                <PenLine size={15} />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-cwhite3 hover:text-cred hover:bg-cred/15 rounded-lg transition-colors"
                                onClick={() => {
                                  setConditionToDelete(condition);
                                  setDeleteDialogOpen(true);
                                }}
                                title="Delete Post Code Condition"
                              >
                                <Trash2 size={15} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>

            {/* Table Pagination & Counter */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
              <div className="text-xs text-cwhite3">
                Showing{" "}
                <span className="font-medium text-cwhite">
                  {conditions.length > 0 ? startIndex + 1 : 0}
                </span>{" "}
                to{" "}
                <span className="font-medium text-cwhite">
                  {Math.min(
                    startIndex + conditions.length,
                    pagination?.total || conditions.length
                  )}
                </span>{" "}
                of{" "}
                <span className="font-medium text-cyellow">
                  {pagination?.total || conditions.length}
                </span>{" "}
                postcodes
              </div>

              <Paginat
                pagination={pagination}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>
              Configuring {totalSelected} selected postcode{totalSelected === 1 ? "" : "s"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-cwhite3/80 mb-2">
            Total selected postcode count: {bulkOverrideMeta?.totalSelectedCount ?? selectedPostcodeTotalCount}
          </div>
          <div className="text-xs text-cwhite3/80 mb-4">
            Total server postcode count: {bulkOverrideMeta?.totalServerCount ?? postcodesMeta?.total ?? postcodes.length}
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-cwhite3">
                Min Order value<span className="text-cred">*</span>
              </label>
              <Input
                value={bulkForm.minimum_order}
                onChange={(event) =>
                  setBulkForm((prev) => ({
                    ...prev,
                    minimum_order: event.target.value,
                  }))
                }
                placeholder="Enter min order value"
                type="number"
                className="mt-1 h-10"
              />
            </div>
            <div>
              <label className="text-sm text-cwhite3">Free Delivery Above</label>
              <Input
                value={bulkForm.free_delivery_above}
                onChange={(event) =>
                  setBulkForm((prev) => ({
                    ...prev,
                    free_delivery_above: event.target.value,
                  }))
                }
                placeholder="0.00"
                type="number"
                className="mt-1 h-10"
              />
            </div>
            <div>
              <label className="text-sm text-cwhite3">
                Charge type <span className="text-cyellow">(delivery charge and driver fee)</span>
              </label>
              <Select
                value={bulkForm.charge_type}
                onValueChange={(value) =>
                  setBulkForm((prev) => ({ ...prev, charge_type: value }))
                }
              >
                <SelectTrigger className="mt-1 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHARGE_TYPES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {bulkForm.charge_type === "exclude" && (
              <>
                <div>
                  <label className="text-sm text-cwhite3">Delivery Charge</label>
                  <Input
                    value={bulkForm.charge}
                    onChange={(event) =>
                      setBulkForm((prev) => ({
                        ...prev,
                        charge: event.target.value,
                      }))
                    }
                    placeholder="0.00"
                    type="number"
                    className="mt-1 h-10"
                  />
                </div>
                <div>
                  <label className="text-sm text-cwhite3">Driver fee</label>
                  <Input
                    value={bulkForm.driver_fee}
                    onChange={(event) =>
                      setBulkForm((prev) => ({
                        ...prev,
                        driver_fee: event.target.value,
                      }))
                    }
                    placeholder="0.00"
                    type="number"
                    className="mt-1 h-10"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="pt-4">
            <div className="flex w-full justify-between gap-3">
              <Button
                type="button"
                className="h-10 flex-1 bg-cred text-white"
                onClick={() => setBulkDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="h-10 flex-1"
                onClick={submitBulkConfigure}
                isLoading={createLoading}
              >
                Confirm
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={postcodesLoading} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Fetching postcode counts</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-3 rounded-md border border-neutral-700/60 bg-cblack2 px-4 py-3">
            <Loader2 className="h-5 w-5 animate-spin text-cyellow" />
            <div className="text-sm text-cwhite3">
              {postcodesMeta?.total
                ? `Total post code count ${postcodesMeta.total} is setting up, please wait.`
                : "Post code count is setting up, please wait."}
            </div>
          </div>
          <div className="text-xs text-cwhite3/80">
            This may take a moment depending on the distance range.
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={createLoading} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Configuring postcodes</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-3 rounded-md border border-neutral-700/60 bg-cblack2 px-4 py-3">
            <Loader2 className="h-5 w-5 animate-spin text-cyellow" />
            <div className="text-sm text-cwhite3">
              Setting up delivery conditions, please wait.
            </div>
          </div>
          <div className="text-xs text-cwhite3/80">
            This may take a moment depending on the number of postcodes.
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>
              {editTarget ? "Edit Postcode" : "Add Postcode"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-cwhite3">Postcode</label>
              <Input
                value={editForm.post_code}
                onChange={(event) =>
                  setEditForm((prev) => ({
                    ...prev,
                    post_code: event.target.value,
                  }))
                }
                placeholder="Enter postcode"
                className="mt-1 h-10"
              />
            </div>
            <div>
              <label className="text-sm text-cwhite3">
                Min Order Value<span className="text-cred">*</span>
              </label>
              <Input
                value={editForm.minimum_order}
                onChange={(event) =>
                  setEditForm((prev) => ({
                    ...prev,
                    minimum_order: event.target.value,
                  }))
                }
                placeholder="Enter min order value"
                type="number"
                className="mt-1 h-10"
              />
            </div>
            <div>
              <label className="text-sm text-cwhite3">Free Delivery Above</label>
              <Input
                value={editForm.free_delivery_above}
                onChange={(event) =>
                  setEditForm((prev) => ({
                    ...prev,
                    free_delivery_above: event.target.value,
                  }))
                }
                placeholder="0.00"
                type="number"
                className="mt-1 h-10"
              />
            </div>
            <div>
              <label className="text-sm text-cwhite3">
                Charge Type <span className="text-cyellow">(Delivery charge and driver fee)</span>
              </label>
              <Select
                value={editForm.charge_type}
                onValueChange={(value) =>
                  setEditForm((prev) => ({ ...prev, charge_type: value }))
                }
              >
                <SelectTrigger className="mt-1 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHARGE_TYPES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editForm.charge_type === "exclude" && (
              <>
                <div>
                  <label className="text-sm text-cwhite3">Delivery Charge</label>
                  <Input
                    value={editForm.charge}
                    onChange={(event) =>
                      setEditForm((prev) => ({
                        ...prev,
                        charge: event.target.value,
                      }))
                    }
                    placeholder="0.00"
                    type="number"
                    className="mt-1 h-10"
                  />
                </div>
                <div>
                  <label className="text-sm text-cwhite3">Driver fee</label>
                  <Input
                    value={editForm.driver_fee}
                    onChange={(event) =>
                      setEditForm((prev) => ({
                        ...prev,
                        driver_fee: event.target.value,
                      }))
                    }
                    placeholder="0.00"
                    type="number"
                    className="mt-1 h-10"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="pt-4">
            <div className="flex w-full justify-between gap-3">
              <Button
                type="button"
                className="h-10 flex-1 bg-cred text-white"
                onClick={() => setEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="h-10 flex-1"
                onClick={submitEditForm}
                isLoading={editTarget ? updateLoading : createLoading}
              >
                Confirm
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        yesBtnTxt="Yes, Delete"
        noBtnTxt="Cancel"
        isLoading={deleteLoading}
        title="Delete Delivery Condition"
        description="You want to delete this delivery condition. This action cannot be undone."
      />
    </Layout2>
  );
}

export default DeliveryManagement;
